/**
 * /admin/settings — settings hub.
 * @file src/web/features/admin/pages/settings.tsx
 *
 * Three sections:
 *
 * 1. Appearance (client-only)
 * Theme, dark/light mode, and which dashboard widgets to render.
 * Persists to localStorage and to uikit's ThemeProvider — no API
 * call. Per-user preference; doesn't travel with the account.
 *
 * 2. Email (server-side AppSetting rows)
 * SMTP host / port / user / pass / from / enabled. Any field here
 * goes through /api/settings/admin/:key and is owned by the
 * admin. Password field is masked; everything else surfaces the
 * current value.
 *
 * 3. Everything else (server-side AppSetting rows)
 * Auto-grouped by key prefix: business_*, support_*, feature_*,
 * and "Other" for anything that doesn't match. Each row is one
 * inline edit.
 *
 * TODO: Toggle `isPublic` per row. Schema supports it, UI doesn't.
 * Needs a PATCH endpoint that accepts { value, isPublic }.
 * TODO: SMTP "Send test email" button — useful once Email is live.
 *
 * @see ../../../../../docs/admin-patterns.md §8 settings, §4 admin-api
 * @see https://dev.bloomneo.com/adminapp/settings
 *
 * @llm-rule WHEN: Adding a new admin-editable setting, exposing a new email provider, or changing the grouped-card layout
 * @llm-rule AVOID: Storing secrets in AppSetting — they belong in .env (appkit reads env directly). The Email card writes env via the server for this reason
 * @llm-rule NOTE: saveValue is useCallback'd so child cards (ContactCard, SettingEditor) keep stable handler refs and don't re-render on unrelated state changes
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  PasswordInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from '@bloomneo/uikit';
import { Info, Send, Building2, FileText, MapPin, Landmark, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { SettingsTabs } from '../components/SettingsTabs';
import { ChangePasswordCard } from '../components/ChangePasswordCard';
import { PageLoading } from '../components/PageLoading';
import { adminFetch, adminFetchJson } from '../lib/admin-api';

interface SettingRow {
  key: string;
  value: string;
  isPublic: boolean;
  description: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

/**
 * Email config is stored in env (.env file / platform secrets), not
 * in the AppSetting table. Appkit's email module reads env directly,
 * so the UI here edits env to match. The allow-list of keys we let
 * the admin edit lives server-side in settings.route.ts::EMAIL_ENV_KEYS.
 */
type EmailStrategy = 'resend' | 'smtp' | 'console';

interface EmailEnv {
  values: Record<string, string>;
  persistence: 'reliable' | 'platform-managed' | 'unknown';
}

const GROUP_FOR_PREFIX: Array<{ prefix: string; label: string }> = [
  { prefix: 'business_', label: 'Business' },
  { prefix: 'support_', label: 'Support' },
  { prefix: 'feature_', label: 'Feature flags' },
];

function groupOf(key: string): string {
  for (const g of GROUP_FOR_PREFIX) {
    if (key.startsWith(g.prefix)) return g.label;
  }
  return 'Other';
}

export default function AdminSettings() {
  const [rows, setRows] = useState<SettingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Server-wide settings are admin.system only. Professional admins get a 403 —
  // that's expected, so we hide those cards rather than showing an error.
  const [noServerAccess, setNoServerAccess] = useState(false);

  useEffect(() => {
    adminFetchJson<{ items: SettingRow[] }>('/api/settings/admin/list')
      .then((data) => setRows(data.items))
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Failed to load';
        if (/^40[13]\b/.test(msg)) setNoServerAccess(true);
        else setError(msg);
      });
  }, []);

  /**
   * AppSetting rows bucketed by prefix for the existing
   * Business / Feature-flags / Other cards.
   *
   * Filtered OUT:
   * - `email_*` — legacy (email config lives in env now)
   * - `support_*` — rendered in the Contact card instead
   * - `contact_*` — same
   * so the admin sees each setting exactly once.
   */
  const grouped = useMemo(() => {
    if (!rows) return null;
    const out: Record<string, SettingRow[]> = {};
    for (const row of rows) {
      if (row.key.startsWith('email_')) continue;
      if (row.key.startsWith('support_')) continue;
      if (row.key.startsWith('contact_')) continue;
      const g = groupOf(row.key);
      (out[g] ??= []).push(row);
    }
    return out;
  }, [rows]);

  // Stable handler so memoized children (ContactCard, SettingEditor)
  // don't see a new function reference on every parent render.
  const saveValue = useCallback(
    async (key: string, value: string): Promise<SettingRow> => {
      const saved = await adminFetchJson<SettingRow>(
        `/api/settings/admin/${encodeURIComponent(key)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ value }),
        },
      );
      setRows((prev) => {
        if (!prev) return [saved];
        const idx = prev.findIndex((r) => r.key === key);
        if (idx === -1) return [...prev, saved];
        return prev.map((r) => (r.key === key ? saved : r));
      });
      return saved;
    },
    [],
  );

  // Wrap saveValue with toast once, up here. Every card gets the same
  // handler identity, and no one has to repeat the `.then(toast.success)`
  // dance at every call site.
  const saveWithToast = useCallback(
    (k: string, v: string) =>
      saveValue(k, v).then((saved) => {
        toast.success(`Saved ${k}`);
        return saved;
      }),
    [saveValue],
  );

  return (
    <>
      <SEO title="Settings" />
      <AdminPageHeader
        title="Settings"
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Settings' }]}
      />
      <SettingsTabs />
      <div className="space-y-6">
        {/* Professional profile — the Professional's own organisation details,
            editable here (plan/login/status stay platform-controlled). */}
        <ProfessionalProfileCard />

        {/* Change password — any logged-in user can rotate their own
            credential; not gated on server access like the cards below. */}
        <ChangePasswordCard />

        {error && (
          <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">
            Failed to load: {error}
          </div>
        )}

        {/* Server-wide settings — admin.system only. Hidden for professional admins. */}
        {!noServerAccess && !error && !rows && <PageLoading label="Loading settings" />}
        {rows && (
          <>
            {/* Email — writes to .env via the server. */}
            <EmailCard />

            {/* Contact — form toggle + recipient + public support details. */}
            <ContactCard rows={rows} onSave={saveWithToast} />

            {/* Everything else, grouped by prefix. One Save per group. */}
            {Object.entries(grouped!).map(([group, groupRows]) => (
              <GroupedSettingsCard
                key={group}
                group={group}
                rows={groupRows}
                onSave={saveWithToast}
              />
            ))}
          </>
        )}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Professional profile (oddits) */
/* -------------------------------------------------------------------------- */

interface ProfessionalProfile {
  plan: string; status: string; ownerEmail: string;
  name: string; ownerName: string | null; ownerPhone: string | null;
  legalName: string | null; billingAddress: string | null; registeredAddress: string | null;
  city: string | null; state: string | null; pincode: string | null;
  gstNumber: string | null; gstStatus: string | null; panNumber: string | null;
  bankName: string | null; bankAccountNumber: string | null; bankIfsc: string | null; bankBranch: string | null;
}

const PROFESSIONAL_EDITABLE = [
  'name', 'legalName', 'ownerName', 'ownerPhone',
  'gstNumber', 'gstStatus', 'panNumber',
  'billingAddress', 'registeredAddress', 'city', 'state', 'pincode',
  'bankName', 'bankAccountNumber', 'bankIfsc', 'bankBranch',
] as const;
type ProfessionalField = (typeof PROFESSIONAL_EDITABLE)[number];
const GST_STATUSES = ['Regular', 'Composition', 'Unregistered', 'SEZ'];

function ProfessionalProfileCard() {
  const [profile, setProfile] = useState<ProfessionalProfile | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [draft, setDraft] = useState<Record<ProfessionalField, string>>({} as Record<ProfessionalField, string>);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminFetchJson<ProfessionalProfile>('/api/professional/profile')
      .then((p) => {
        setProfile(p);
        const d = {} as Record<ProfessionalField, string>;
        for (const f of PROFESSIONAL_EDITABLE) d[f] = (p as any)[f] ?? '';
        setDraft(d);
      })
      .catch(() => setNotFound(true));
  }, []);

  // Only professional admins have a profile; hide the card entirely otherwise.
  if (notFound) return null;
  if (!profile) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-paper p-6"><PageLoading label="Loading professional profile" /></div>
    );
  }

  const initial = {} as Record<ProfessionalField, string>;
  for (const f of PROFESSIONAL_EDITABLE) initial[f] = (profile as any)[f] ?? '';
  const dirty = PROFESSIONAL_EDITABLE.some((f) => draft[f] !== initial[f]);
  const set = (f: ProfessionalField, v: string) => setDraft((d) => ({ ...d, [f]: v }));

  async function save() {
    if (!draft.name.trim()) { toast.error('Organization name is required'); return; }
    setSaving(true);
    try {
      await adminFetchJson('/api/professional/profile', { method: 'PATCH', body: JSON.stringify(draft) });
      setProfile((p) => (p ? { ...p, ...(draft as any) } : p));
      toast.success('Professional profile saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-paper p-6">
      <div className="mb-5">
        <h3 className="text-base font-semibold text-ink-900">Professional profile</h3>
        <p className="mt-1 text-sm text-ink-500">Your organisation, tax, address and bank details. Plan and login email are managed by oddits.</p>
      </div>

      <div className="space-y-5">
        <FSection title="Organization" icon={Building2}>
          <FInput label="Professional organization name" value={draft.name} onChange={(v) => set('name', v)} />
          <FInput label="Legal name" value={draft.legalName} onChange={(v) => set('legalName', v)} />
          <FInput label="Owner name" value={draft.ownerName} onChange={(v) => set('ownerName', v)} />
          <FInput label="Phone" value={draft.ownerPhone} onChange={(v) => set('ownerPhone', v)} />
          <FReadOnly label="Login email" value={profile.ownerEmail} />
          <FReadOnly label="Plan" value={profile.plan} capitalize />
        </FSection>

        <FSection title="Tax" icon={FileText}>
          <FInput label="GST number" value={draft.gstNumber} onChange={(v) => set('gstNumber', v)} />
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-ink-700">GST status</label>
            <select value={draft.gstStatus} onChange={(e) => set('gstStatus', e.target.value)}
              style={{ outline: 'none', boxShadow: 'none' }}
              className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-2.5 text-sm text-ink-900 focus:border-ink-300 focus-visible:outline-none">
              <option value="">—</option>
              {GST_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <FInput label="PAN" value={draft.panNumber} onChange={(v) => set('panNumber', v)} />
        </FSection>

        <FSection title="Address" icon={MapPin}>
          <FInput label="Billing address" value={draft.billingAddress} onChange={(v) => set('billingAddress', v)} />
          <FInput label="Registered address" value={draft.registeredAddress} onChange={(v) => set('registeredAddress', v)} />
          <FInput label="City" value={draft.city} onChange={(v) => set('city', v)} />
          <FInput label="State" value={draft.state} onChange={(v) => set('state', v)} />
          <FInput label="Pincode" value={draft.pincode} onChange={(v) => set('pincode', v)} />
        </FSection>

        <FSection title="Bank" icon={Landmark}>
          <FInput label="Bank name" value={draft.bankName} onChange={(v) => set('bankName', v)} />
          <FInput label="Account number" value={draft.bankAccountNumber} onChange={(v) => set('bankAccountNumber', v)} />
          <FInput label="IFSC code" value={draft.bankIfsc} onChange={(v) => set('bankIfsc', v)} />
          <FInput label="Location" value={draft.bankBranch} onChange={(v) => set('bankBranch', v)} />
        </FSection>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2 border-t border-ink-100 pt-4">
        <button onClick={() => setDraft(initial)} disabled={!dirty || saving}
          className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50">Reset</button>
        <button onClick={save} disabled={!dirty || saving}
          className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
        </button>
      </div>
    </div>
  );
}

function FSection({ title, icon: Icon, children }: { title: string; icon: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-200 p-4">
      <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function FInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-2.5">
      <label className="text-sm font-medium text-ink-700">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        style={{ outline: 'none', boxShadow: 'none' }}
        className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none" />
    </div>
  );
}

function FReadOnly({ label, value, capitalize }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div className="space-y-2.5">
      <label className="text-sm font-medium text-ink-700">{label}</label>
      <input value={value} readOnly disabled
        className={`w-full rounded-xl border border-ink-200 bg-ink-50 px-4 py-2.5 text-sm text-ink-400 ${capitalize ? 'capitalize' : ''}`} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Email */
/* -------------------------------------------------------------------------- */

/**
 * Email card — edits the email-related env vars that appkit reads.
 * Fields shown depend on the selected strategy:
 * - resend → RESEND_API_KEY
 * - smtp → SMTP_HOST / SMTP_PORT / SMTP_USER / SMTP_PASS
 * - console → nothing extra (emails get logged to stdout; dev-only)
 *
 * "From" address is shared across strategies. Save button writes
 * every dirty key in one API call. Test-send button hits a separate
 * endpoint and surfaces appkit's error message directly if the
 * provider rejects the attempt.
 */
function EmailCard() {
  const [state, setState] = useState<EmailEnv | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    adminFetchJson<EmailEnv>('/api/settings/admin/email-env')
      .then((data) => {
        setState(data);
        setDraft({ ...data.values });
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Failed to load email config'),
      );
  }, []);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }
  if (!state) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email</CardTitle>
        </CardHeader>
        <CardContent>
          <PageLoading label="Loading email config" />
        </CardContent>
      </Card>
    );
  }

  const strategy: EmailStrategy =
    (draft.BLOOM_EMAIL_STRATEGY || state.values.BLOOM_EMAIL_STRATEGY || 'console') as EmailStrategy;

  const dirty = Object.entries(draft).some(
    ([k, v]) => (state.values[k] ?? '') !== v,
  );

  async function save() {
    setSaving(true);
    try {
      const next = await adminFetchJson<EmailEnv>('/api/settings/admin/email-env', {
        method: 'PUT',
        body: JSON.stringify(draft),
      });
      setState(next);
      setDraft({ ...next.values });
      toast.success('Email config saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testTo.trim()) {
      toast.error('Enter an address first');
      return;
    }
    setTesting(true);
    try {
      const res = await adminFetch('/api/settings/admin/email-test', {
        method: 'POST',
        body: JSON.stringify({ to: testTo.trim() }),
      });
      const body = await res.json();
      if (!res.ok || body.ok === false) {
        toast.error('Send failed: ' + (body.error ?? res.statusText));
      } else {
        toast.success('Test email sent to ' + testTo.trim());
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setTesting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email</CardTitle>
        <CardDescription>
          Outbound email provider. Appkit reads these values from env
          at boot — saving here writes them to the project's <code>.env</code>
          and to the running process.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Persistence disclaimer — driven by the server's detection.
            On DigitalOcean droplets / VMs / bare metal the write
            survives a restart; on Vercel / Fly / Lambda-style hosts
            it won't and the admin should also set the env var via
            the hosting platform. */}
        {state.persistence !== 'reliable' && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>
              {state.persistence === 'platform-managed'
                ? 'Detected a container/serverless host'
                : 'Check how your server reads env'}
            </AlertTitle>
            <AlertDescription>
              {state.persistence === 'platform-managed'
                ? 'Saving here writes .env, but container platforms typically ignore .env on boot. Set the same values via your host (Fly secrets, Vercel env, etc.) so they survive a deploy.'
                : 'Saving writes to .env and the running process. On DigitalOcean droplets and similar hosts this persists. On serverless/container hosts, also set the same values via your platform.'}
            </AlertDescription>
          </Alert>
        )}

        {/* Strategy picker. Changing it re-renders the field list
            below without saving — the admin has to hit Save to commit. */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Provider strategy</label>
          <Select
            value={strategy}
            onValueChange={(v) =>
              setDraft((d) => ({ ...d, BLOOM_EMAIL_STRATEGY: v }))
            }
          >
            <SelectTrigger className="w-full max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resend">Resend (recommended)</SelectItem>
              <SelectItem value="smtp">SMTP (any provider — Mailgun, SendGrid, Postmark, etc.)</SelectItem>
              <SelectItem value="console">Console (log to stdout — dev only)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {strategy === 'resend' && (
              <>Create a free key at <a href="https://resend.com" className="underline" target="_blank" rel="noreferrer">resend.com</a> — the simplest path to production.</>
            )}
            {strategy === 'smtp' && (
              <>Works with any SMTP provider. Mailgun, SendGrid, Postmark, Gmail — point SMTP_HOST at their relay and fill in the credentials.</>
            )}
            {strategy === 'console' && (
              <>No emails actually sent. Appkit logs them to the server console. Useful for local dev before you've picked a provider.</>
            )}
          </p>
        </div>

        {/* Common fields — "from" name + address. Used by every strategy. */}
        <div className="grid gap-4 md:grid-cols-2">
          <LabeledInput
            label="From name"
            placeholder="App"
            value={draft.BLOOM_EMAIL_FROM_NAME ?? ''}
            onChange={(v) => setDraft((d) => ({ ...d, BLOOM_EMAIL_FROM_NAME: v }))}
          />
          <LabeledInput
            label="From email"
            placeholder="no-reply@example.com"
            value={draft.BLOOM_EMAIL_FROM_EMAIL ?? ''}
            onChange={(v) => setDraft((d) => ({ ...d, BLOOM_EMAIL_FROM_EMAIL: v }))}
          />
        </div>

        {/* Strategy-specific fields */}
        {strategy === 'resend' && (
          <LabeledInput
            label="Resend API key"
            type="password"
            placeholder="re_…"
            value={draft.RESEND_API_KEY ?? ''}
            onChange={(v) => setDraft((d) => ({ ...d, RESEND_API_KEY: v }))}
            help='Starts with "re_". Leave the field as "********" to keep the existing key.'
          />
        )}

        {strategy === 'smtp' && (
          <div className="grid gap-4 md:grid-cols-2">
            <LabeledInput
              label="SMTP host"
              placeholder="smtp.mailgun.org"
              value={draft.SMTP_HOST ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, SMTP_HOST: v }))}
            />
            <LabeledInput
              label="SMTP port"
              placeholder="587"
              value={draft.SMTP_PORT ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, SMTP_PORT: v }))}
            />
            <LabeledInput
              label="SMTP username"
              placeholder="apikey"
              value={draft.SMTP_USER ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, SMTP_USER: v }))}
            />
            <LabeledInput
              label="SMTP password"
              type="password"
              placeholder="••••••••"
              value={draft.SMTP_PASS ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, SMTP_PASS: v }))}
              help='Leave as "********" to keep the existing password.'
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save email config'}
          </Button>
        </div>

        {/* Test send — always available. Fails with the provider's
            error message if the config is bad, so misconfig is
            obvious before the admin waits for a real signup/reset
            flow to break. */}
        <div className="space-y-2 pt-2 border-t">
          <label className="text-sm font-medium">Send test email</label>
          <p className="text-xs text-muted-foreground">
            Sends a one-line test via the current config. Useful right
            after changing the provider or rotating keys.
          </p>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="you@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className="flex-1 max-w-sm"
            />
            <Button
              variant="outline"
              onClick={sendTest}
              disabled={testing || !testTo.trim()}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {testing ? 'Sending…' : 'Send test'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type,
  help,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  type?: 'password' | 'text';
  help?: string;
}) {
  const InputEl = type === 'password' ? PasswordInput : Input;
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      <InputEl
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Contact */
/* -------------------------------------------------------------------------- */

/**
 * Contact card — form enable toggle + recipient + the support details
 * shown on the public /contact page.
 *
 * Why one card: admins reason about "contact-y things" as one concern
 * (customers want to reach us → where does the form go, what's the
 * support email, etc.). Splitting these across three cards in earlier
 * iterations confused users who wanted to change the support number
 * and hunted through "Feature flags" and "Business" before finding it.
 *
 * Why one Save button: typing a phone number and tabbing away should
 * NOT fire a network call; admins batch edits ("fix phone, update
 * hours, flip the form on"). Fields track local draft state; clicking
 * Save walks the dirty set and issues one PUT per changed key.
 *
 * `contact_form_to_email` stays server-side only (isPublic=false). The
 * public endpoint exposes only the `enabled` flag — a compromised
 * browser can't redirect intake to an attacker-controlled inbox.
 */
const CONTACT_KEYS = [
  'contact_form_enabled',
  'contact_form_to_email',
  'support_email',
  'support_phone',
  'support_hours',
  'support_whatsapp_url',
  'support_address',
] as const;

type ContactKey = (typeof CONTACT_KEYS)[number];

function ContactCard({
  rows,
  onSave,
}: {
  rows: SettingRow[];
  onSave: (key: string, value: string) => Promise<SettingRow>;
}) {
  // Build the initial draft from server rows. Re-synced whenever the
  // underlying `rows` change (e.g. after a successful save flushes
  // back from the parent).
  const initial = useMemo(() => {
    const byKey = new Map<string, SettingRow>();
    for (const row of rows) byKey.set(row.key, row);
    const out = {} as Record<ContactKey, string>;
    for (const key of CONTACT_KEYS) {
      out[key] = byKey.get(key)?.value ?? '';
    }
    return out;
  }, [rows]);

  const [draft, setDraft] = useState<Record<ContactKey, string>>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = CONTACT_KEYS.some((k) => draft[k] !== initial[k]);
  const enabled = (draft.contact_form_enabled || 'false').toLowerCase() === 'true';

  function set<K extends ContactKey>(key: K, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function saveAll() {
    setSaving(true);
    // Walk only changed keys. Fire in parallel — each PUT is
    // independent, the server audits each write separately, and the
    // UI only cares that they all complete before re-enabling Save.
    const changed = CONTACT_KEYS.filter((k) => draft[k] !== initial[k]);
    try {
      await Promise.all(changed.map((k) => onSave(k, draft[k])));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setDraft(initial);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact</CardTitle>
        <CardDescription>
          Public contact page details and the contact-form recipient.
          Changes are visible on <code>/contact</code> after a reload.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Form toggle — part of the same draft, saved on click of the
            single Save button below. */}
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <p className="text-sm font-medium">Contact form</p>
            <p className="text-xs text-muted-foreground">
              When enabled, the /contact page renders a form that posts
              to /api/contact-message. Delivery uses your configured
              Email provider.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) =>
              set('contact_form_enabled', v ? 'true' : 'false')
            }
          />
        </div>

        {/* Recipient — only shown when the form is on */}
        {enabled && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Form recipient email</label>
            <Input
              value={draft.contact_form_to_email}
              placeholder="support@example.com"
              onChange={(e) => set('contact_form_to_email', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Where contact-form submissions land. Falls back to Support
              email if empty.
            </p>
          </div>
        )}

        {/* Support details — same fields as the public /contact page */}
        <div className="grid gap-4 md:grid-cols-2">
          <PlainField
            label="Support email"
            placeholder="support@example.com"
            value={draft.support_email}
            onChange={(v) => set('support_email', v)}
          />
          <PlainField
            label="Support phone"
            placeholder="+1 555-0100"
            value={draft.support_phone}
            onChange={(v) => set('support_phone', v)}
          />
          <PlainField
            label="Support hours"
            placeholder="Mon–Fri, 9am–6pm IST"
            value={draft.support_hours}
            onChange={(v) => set('support_hours', v)}
          />
          <PlainField
            label="WhatsApp link"
            placeholder="https://wa.me/15550100"
            value={draft.support_whatsapp_url}
            onChange={(v) => set('support_whatsapp_url', v)}
          />
          <div className="md:col-span-2">
            <PlainField
              label="Mailing address"
              placeholder="123 Example St, City, Country"
              value={draft.support_address}
              onChange={(v) => set('support_address', v)}
            />
          </div>
        </div>

        {/* Single Save button for the whole card. Disabled when the
            draft matches the server state — no "save" when nothing
            changed. Reset reverts local draft back to server values. */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={reset}
            disabled={!dirty || saving}
          >
            Reset
          </Button>
          <Button size="sm" onClick={saveAll} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Labeled input that just reads/writes a string — no per-field save button. */
function PlainField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Other AppSetting rows */
/* -------------------------------------------------------------------------- */

/**
 * Card for one prefix group (Business / Feature flags / Other).
 * Every row is an inline edit; a single Save button at the card
 * footer walks only the changed rows and fires one PUT per dirty key.
 */
function GroupedSettingsCard({
  group,
  rows,
  onSave,
}: {
  group: string;
  rows: SettingRow[];
  onSave: (key: string, value: string) => Promise<SettingRow>;
}) {
  const initial = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of rows) out[row.key] = row.value;
    return out;
  }, [rows]);

  const [draft, setDraft] = useState<Record<string, string>>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = rows.some((r) => (draft[r.key] ?? '') !== (initial[r.key] ?? ''));

  async function saveAll() {
    setSaving(true);
    const changed = rows.filter(
      (r) => (draft[r.key] ?? '') !== (initial[r.key] ?? ''),
    );
    try {
      await Promise.all(changed.map((r) => onSave(r.key, draft[r.key] ?? '')));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{group}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex flex-col gap-2 border-b pb-4 last:border-b-0 last:pb-0"
          >
            <div className="flex items-center gap-2">
              <code className="font-mono text-xs">{row.key}</code>
              {row.isPublic && (
                <Badge variant="outline" className="text-xs">public</Badge>
              )}
            </div>
            {row.description && (
              <p className="text-xs text-muted-foreground">{row.description}</p>
            )}
            <Input
              value={draft[row.key] ?? ''}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, [row.key]: e.target.value }))
              }
            />
          </div>
        ))}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDraft(initial)}
            disabled={!dirty || saving}
          >
            Reset
          </Button>
          <Button size="sm" onClick={saveAll} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
